import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, FileText, MessageSquare, Star, Ambulance, ShieldCheck, MapPin, Clock, ArrowRight, Heart, Car, BedDouble } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AnimatedCounter } from '@/components/ui/AnimatedCounter';
import { Reveal } from '@/components/ui/Reveal';
import { StarRating } from '@/components/ui/StarRating';
import CPAMBadge from '@/components/ui/CPAMBadge';
import { useSEO, buildBreadcrumb, buildFAQPage, buildOrganization, buildWebSite, SITE_URL } from '@/lib/seo';
import api from '@/lib/api';

const MAIN_PHONE = '04 70 42 72 63';
const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_ambulances-beaulon/artifacts/5zctf2x4_file_000000005b88720a8aa856385a7438f6.png';

export default function Home() {
  useSEO({
    title: "Groupe Ambulances Beaulon | Transport sanitaire dans l'Allier (03)",
    description: "Le Groupe Ambulances Beaulon assure vos transports sanitaires dans l'Allier avec professionnalisme, réactivité et accompagnement humain. Ambulances, VSL, taxis conventionnés, transport bariatrique. 4 implantations locales : Garnat, Avermes, Diou, Tronget.",
    keywords: "ambulance allier, ambulance moulins, ambulance avermes, ambulance saint ennemond, ambulance garnat, ambulance diou, ambulance tronget, ambulance le montet, taxi conventionné allier, VSL allier, transport sanitaire conventionné, transport bariatrique, transport médical allier, ambulance conventionnée CPAM",
    canonical: `${SITE_URL}/`,
    ogImage: HERO_IMAGE,
    ogType: 'website',
    jsonLd: [
      buildOrganization(),
      buildWebSite(),
      buildBreadcrumb([
        { name: 'Accueil', url: SITE_URL + '/' },
      ]),
      buildFAQPage([
        { q: 'Quelles zones couvre le Groupe Ambulances Beaulon ?', a: `Nous couvrons tout le département de l'Allier (03) grâce à nos 4 implantations locales : Ambulances Beaulon à Garnat-sur-Engièvre (nord-est de l'Allier), Ambulance 03 à Avermes / Saint-Ennemond (secteur Moulins), Céline Ambulance à Diou (Val de Besbre) et Ambulance Tronget – Le Montet (sud-ouest de l'Allier). Nous intervenons également en Saône-et-Loire limitrophe selon disponibilité.` },
        { q: 'Vos ambulances et VSL sont-ils conventionnés CPAM ?', a: `Oui. Toutes nos ambulances et tous nos VSL sont conventionnés par l'Assurance Maladie. Nous appliquons la prise en charge directe (tiers payant) pour les patients éligibles : dialyse, chimiothérapie, radiothérapie, hospitalisations et consultations sur prescription.` },
        { q: 'Comment réserver un transport sanitaire ?', a: `Appelez l'implantation la plus proche : Ambulances Beaulon (Garnat) au 04 70 42 72 63, Ambulance 03 (Avermes) au 06 56 66 05 71, Céline Ambulance (Diou) au 04 70 42 90 08 ou Ambulance Tronget – Le Montet au 04 70 47 10 30. Vous pouvez aussi remplir notre formulaire de devis en ligne.` },
        { q: 'Une prescription médicale est-elle obligatoire pour un remboursement ?', a: `Oui. Pour être remboursé par l'Assurance Maladie, une prescription médicale (bon de transport CERFA n°50742*04) délivrée par votre médecin est obligatoire, sauf en cas d'urgence vitale. Elle indique le mode de transport à utiliser (ambulance, VSL, taxi conventionné).` },
        { q: 'Quelle est la différence entre une ambulance et un VSL ?', a: `L'ambulance est utilisée lorsque le patient doit être transporté en position allongée ou nécessite une surveillance médicale. Le VSL (Véhicule Sanitaire Léger) est adapté aux patients pouvant voyager en position assise mais nécessitant une assistance. Le choix est indiqué par votre médecin.` },
      ]),
    ],
  });

  // Stats : les 4 implantations physiques sont fixes (Garnat, Avermes/St-Ennemond, Diou, Tronget/Le Montet).
  // Les autres compteurs proviennent du backend (véhicules), avec valeur initiale de fallback pour éviter
  // tout affichage "0" au premier rendu (SEO / mobile / reduced-motion).
  const [stats, setStats] = useState({ ambulances: 6, vsl: 8, taxis: 13, implantations: 4 });
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get('/stats').then(r => {
      // Forcer 4 implantations physiques quel que soit le backend (source de vérité SEO front)
      setStats({ ...r.data, implantations: 4 });
    }).catch(() => {});
    api.get('/avis').then(r => setReviews(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden hero-wash" data-testid="hero-section">
        <div className="container-app pt-12 sm:pt-16 pb-16 lg:pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="flex items-center gap-2 mb-5 flex-wrap">
                <div className="inline-flex items-center gap-2 rounded-full bg-white border border-brand-100 px-3 py-1.5 shadow-sm">
                  <ShieldCheck className="h-3.5 w-3.5 text-brand-700" />
                  <span className="text-xs font-medium text-brand-800 uppercase tracking-[0.14em]">Groupe Ambulances Beaulon</span>
                </div>
                <CPAMBadge variant="solid" size="sm" />
              </div>
            </Reveal>
            <Reveal delay={60}>
              <div className="mb-4" data-testid="hero-brand-title">
                <h2 className="font-heading tracking-tight leading-[1.05]">
                  {/* "Groupe Ambulances" — dark, medium-bold. Same font family as "Beaulon". */}
                  <span className="block sm:inline text-anthracite-900 font-semibold text-3xl sm:text-4xl lg:text-5xl">
                    Groupe Ambulances
                  </span>{' '}
                  {/* "Beaulon" — brand accent, slightly bolder. Size ratio ~1.15× (within 1.2–1.3× cap). */}
                  <span className="block sm:inline text-brand-800 font-extrabold text-4xl sm:text-5xl lg:text-6xl">
                    Beaulon
                  </span>
                </h2>
                <div className="mt-4 h-1 w-16 rounded-full bg-brand-500"></div>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <h1 className="h1 text-anthracite-950 mt-5" data-testid="hero-title">
                Transport sanitaire de <span className="text-brand-700">proximité</span> dans l'Allier
              </h1>
            </Reveal>
            <Reveal delay={180}>
              <p className="lead mt-5 max-w-2xl">
                Le <strong className="text-anthracite-900 font-semibold">Groupe Ambulances Beaulon</strong> assure vos transports sanitaires avec professionnalisme, réactivité et accompagnement humain. <span className="whitespace-nowrap">Ambulance Beaulon</span> &amp; <span className="whitespace-nowrap">Ambulance 03 Avermes Saint-Ennemond</span>.
              </p>
            </Reveal>
            <Reveal delay={220}>
              <div className="mt-7 flex flex-wrap gap-3">
                <a
                  href={`tel:${MAIN_PHONE.replace(/\s/g, '')}`}
                  className="inline-flex items-center gap-2 rounded-xl bg-brand-500 px-5 py-3 text-white font-medium shadow-btn hover:bg-brand-600 transition-all hover:-translate-y-0.5"
                  data-testid="hero-call-button"
                >
                  <Phone className="h-5 w-5" /> Appeler {MAIN_PHONE}
                </a>
                <Link to="/devis" className="inline-flex items-center gap-2 rounded-xl bg-white border border-border px-5 py-3 font-medium text-anthracite-900 hover:bg-anthracite-100 transition-colors" data-testid="hero-quote-button">
                  <FileText className="h-5 w-5" /> Demander un devis
                </Link>
                <Link to="/contact" className="inline-flex items-center gap-2 rounded-xl bg-white border border-border px-5 py-3 font-medium text-anthracite-900 hover:bg-anthracite-100" data-testid="hero-contact-button">
                  <MessageSquare className="h-5 w-5" /> Nous écrire
                </Link>
                <Link to="/avis" className="inline-flex items-center gap-2 rounded-xl px-5 py-3 font-medium text-brand-700 hover:bg-brand-50" data-testid="hero-reviews-link">
                  <Star className="h-5 w-5 fill-amber-400 text-amber-400" /> Avis clients
                </Link>
              </div>
            </Reveal>

            {/* Mini-stats inline */}
            <Reveal delay={300}>
              <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-xl">
                <Stat icon={Ambulance} value={stats.ambulances} label="Ambulances" />
                <Stat icon={Car} value={stats.vsl} label="VSL" />
                <Stat icon={Car} value={stats.taxis} label="Taxis conv." />
                <Stat icon={MapPin} value={stats.implantations} label="Implantations" />
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={120}>
              <div className="relative">
                <div className="absolute -inset-3 rounded-3xl bg-gradient-to-tr from-brand-200/40 to-brand-50 blur-2xl"></div>
                <div className="relative aspect-[4/3] lg:aspect-[3/4] rounded-3xl overflow-hidden border bg-white shadow-card">
                  <img
                    src={HERO_IMAGE}
                    alt="Ambulance Beaulon - véhicule de transport sanitaire moderne"
                    className="w-full h-full object-cover lg:object-contain bg-gradient-to-br from-anthracite-100 to-anthracite-200/40 p-2"
                    loading="eager"
                  />
                  <div className="absolute bottom-4 left-4 right-4 rounded-xl bg-white/95 backdrop-blur p-4 border shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                        <Clock className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-anthracite-900">Réactivité &amp; écoute</p>
                        <p className="text-xs text-muted-foreground">Equipes formées, planning organisé</p>
                      </div>
                      <CPAMBadge variant="soft" size="sm" className="hidden sm:inline-flex" />
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* GROUP BRANDING BAND */}
      <section className="relative bg-gradient-to-r from-brand-900 via-brand-800 to-brand-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_20%_50%,rgba(31,122,174,0.5),transparent_50%),radial-gradient(circle_at_80%_50%,rgba(185,217,234,0.2),transparent_50%)]"></div>
        <div className="container-app relative py-8 sm:py-10 grid lg:grid-cols-[auto_1fr_auto] gap-6 items-center">
          {/* Group name big */}
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl bg-white/10 border border-white/20 backdrop-blur flex items-center justify-center">
              <span className="text-white font-heading font-bold text-2xl sm:text-3xl">B</span>
            </div>
            <div className="leading-tight">
              <p className="font-heading font-semibold text-white/85 text-lg sm:text-xl tracking-tight">Groupe Ambulances</p>
              <p className="font-heading font-extrabold text-2xl sm:text-3xl lg:text-4xl tracking-tight -mt-0.5">Beaulon</p>
            </div>
          </div>
          {/* Sub-brands */}
          <div className="flex flex-col sm:flex-row gap-3 lg:justify-center">
            <div className="flex items-center gap-2 rounded-xl bg-white/10 border border-white/15 backdrop-blur px-4 py-2.5">
              <span className="h-2 w-2 rounded-full bg-emerald-400 shrink-0"></span>
              <div className="leading-tight">
                <p className="text-[10px] uppercase tracking-wider text-brand-200">Notre identité historique</p>
                <p className="font-heading font-semibold text-sm">Ambulance Beaulon</p>
              </div>
            </div>
            <div className="flex items-center gap-2 rounded-xl bg-white/10 border border-white/15 backdrop-blur px-4 py-2.5">
              <span className="h-2 w-2 rounded-full bg-emerald-400 shrink-0"></span>
              <div className="leading-tight">
                <p className="text-[10px] uppercase tracking-wider text-brand-200">Région de Moulins</p>
                <p className="font-heading font-semibold text-sm">Ambulance 03 Avermes Saint-Ennemond</p>
              </div>
            </div>
          </div>
          {/* Allier badge */}
          <div className="flex items-center gap-2 text-sm bg-white/10 border border-white/15 backdrop-blur rounded-xl px-4 py-2.5">
            <MapPin className="h-4 w-4 text-brand-200" />
            <span className="font-medium">Département de l'Allier (03)</span>
          </div>
        </div>
      </section>

      {/* COVERAGE STRIP */}
      <section className="border-y bg-white">
        <div className="container-app py-6 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          {[
            { label: 'Garnat-sur-Engièvre', sub: '04 70 42 72 63' },
            { label: 'Diou', sub: '04 70 42 90 08' },
            { label: 'Tronget', sub: '04 70 47 10 30' },
            { label: 'Avermes / Saint-Ennemond', sub: '06 56 66 05 71' },
          ].map((c) => (
            <div key={c.label} className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-brand-600 shrink-0" />
              <div className="min-w-0">
                <p className="font-medium text-anthracite-900 truncate">{c.label}</p>
                <p className="text-xs text-muted-foreground">{c.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ESTIMATEUR CTA — Simulateur unifié Taxi / VSL / Ambulance */}
      <section className="bg-gradient-to-br from-brand-50 via-white to-brand-50/40 border-b">
        <div className="container-app py-8 sm:py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.14em] text-brand-700 font-semibold">Simulateur</p>
            <h2 className="mt-1 text-xl sm:text-2xl font-heading font-semibold text-anthracite-950">
              Combien coûtera votre trajet ?
            </h2>
            <p className="mt-1 text-sm text-anthracite-700 max-w-xl">
              Estimez gratuitement votre transport en Taxi, VSL ou Ambulance en quelques secondes.
            </p>
          </div>
          <Link
            to="/estimation-transport"
            className="inline-flex items-center gap-2 rounded-xl bg-brand-500 hover:bg-brand-600 text-white px-5 py-3 font-medium shadow-btn transition-all hover:-translate-y-0.5"
            data-testid="home-estimation-cta"
          >
            Calculer mon estimation <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* NOS MARQUES DU GROUPE — 4 MARQUES HARMONISÉES */}
      <section className="section-pad bg-gradient-to-br from-brand-50 via-white to-brand-50/50" data-testid="home-brands">
        <div className="container-app">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-10">
              <p className="badge-soft mb-3">Nos marques</p>
              <h2 className="h2 text-anthracite-950">Un groupe, plusieurs identités, une même exigence</h2>
              <p className="lead mt-3">
                Le Groupe Ambulances Beaulon rassemble plusieurs entités complémentaires pour couvrir l'ensemble du département de l'Allier.
              </p>
            </div>
          </Reveal>

          <div className="grid gap-6 md:grid-cols-2">
            {[
              {
                testid: 'home-brand-beaulon',
                kicker: 'Notre identité historique',
                brand: 'Ambulances',
                brandSub: 'Beaulon',
                brandSubSize: 'text-3xl sm:text-4xl lg:text-5xl',
                brandSize: 'text-4xl sm:text-5xl lg:text-[3.25rem]',
                desc: (<>Notre implantation historique à <strong className="text-anthracite-900 font-semibold">Garnat-sur-Engièvre, Beaulon et le nord-est de l'Allier</strong>. Transport sanitaire conventionné en ambulance, VSL et taxi.</>),
                phone: '04 70 42 72 63',
                phoneTel: 'tel:+33470427263',
                ctaLabel: 'Découvrir Ambulances Beaulon',
                ctaLink: '/ambulance-beaulon-garnat',
              },
              {
                testid: 'home-brand-a03',
                kicker: 'Notre implantation à Avermes / Saint-Ennemond',
                brand: 'Ambulance 03',
                brandSub: 'Avermes · Saint-Ennemond',
                brandSubSize: 'text-2xl sm:text-3xl lg:text-4xl',
                brandSize: 'text-4xl sm:text-5xl lg:text-[3.25rem]',
                desc: (<>Transport sanitaire en <strong className="text-anthracite-900 font-semibold">ambulance et VSL</strong> à Avermes, Saint-Ennemond, Yzeure et sur le secteur de Moulins.</>),
                phone: '06 56 66 05 71',
                phoneTel: 'tel:+33656660571',
                ctaLabel: 'Découvrir Ambulance 03',
                ctaLink: '/ambulance-03-avermes',
              },
              {
                testid: 'home-brand-tronget',
                kicker: "Notre implantation dans le sud-ouest",
                brand: 'Ambulance Tronget',
                brandSub: 'Le Montet · Village Thibault',
                brandSubSize: 'text-2xl sm:text-3xl lg:text-4xl',
                brandSize: 'text-4xl sm:text-5xl lg:text-[3.25rem]',
                desc: (<>Transport sanitaire en <strong className="text-anthracite-900 font-semibold">ambulance et VSL</strong> pour Tronget, Le Montet, Noyant-d'Allier, Cressanges et le centre-sud de l'Allier.</>),
                phone: '04 70 47 10 30',
                phoneTel: 'tel:+33470471030',
                ctaLabel: 'Découvrir Ambulance Tronget',
                ctaLink: '/ambulance-tronget-le-montet',
              },
              {
                testid: 'home-brand-celine',
                kicker: 'Notre implantation dans le Val de Besbre',
                brand: 'Céline Ambulance',
                brandSub: 'Diou · Val de Besbre',
                brandSubSize: 'text-2xl sm:text-3xl lg:text-4xl',
                brandSize: 'text-4xl sm:text-5xl lg:text-[3.25rem]',
                desc: (<>Transport sanitaire en <strong className="text-anthracite-900 font-semibold">ambulance et VSL</strong> pour Diou, Dompierre-sur-Besbre, Saligny-sur-Roudon et le Val de Besbre.</>),
                phone: '04 70 42 90 08',
                phoneTel: 'tel:+33470429008',
                ctaLabel: 'Découvrir Céline Ambulance',
                ctaLink: '/celine-ambulance-diou',
              },
            ].map((b, i) => (
              <Reveal key={b.testid} delay={i * 80}>
                <article
                  className="relative rounded-3xl overflow-hidden border-2 border-brand-100 bg-white shadow-card h-full flex flex-col"
                  data-testid={b.testid}
                >
                  <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-brand-100/60 blur-3xl pointer-events-none"></div>
                  <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-brand-200/30 blur-3xl pointer-events-none"></div>
                  <div className="relative p-6 sm:p-8 lg:p-10 flex flex-col flex-1">
                    <p className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.22em] text-brand-700 mb-3">
                      {b.kicker}
                    </p>
                    <h3 className="font-heading font-extrabold tracking-tight leading-[1.05]">
                      <span className={`block text-brand-800 ${b.brandSize}`}>{b.brand}</span>
                      <span className={`block text-anthracite-950 ${b.brandSubSize} mt-2`}>
                        {b.brandSub}
                      </span>
                    </h3>
                    <div className="mt-4 h-1 w-16 rounded-full bg-brand-500"></div>
                    <p className="mt-5 text-base sm:text-lg text-anthracite-700 leading-relaxed flex-1">
                      {b.desc}
                    </p>
                    <p className="mt-5">
                      <a
                        href={b.phoneTel}
                        className="inline-flex items-center gap-2 text-brand-700 hover:text-brand-900 font-heading font-bold text-lg sm:text-xl"
                        data-testid={`${b.testid}-phone`}
                      >
                        <Phone className="h-5 w-5" />
                        {b.phone}
                      </a>
                    </p>
                    <div className="mt-6 flex flex-col sm:flex-row gap-3">
                      <Link
                        to={b.ctaLink}
                        className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-brand-500 to-brand-700 hover:from-brand-600 hover:to-brand-800 text-white px-6 py-3.5 font-semibold shadow-btn transition-all hover:-translate-y-0.5"
                        data-testid={`${b.testid}-cta`}
                      >
                        {b.ctaLabel} <ArrowRight className="h-4 w-4" />
                      </Link>
                      <a
                        href={b.phoneTel}
                        className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-brand-500 text-brand-700 px-6 py-3.5 font-semibold hover:bg-brand-50"
                      >
                        <Phone className="h-4 w-4" /> Appeler
                      </a>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES TEASER */}
      <section className="section-pad">
        <div className="container-app">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end gap-4 justify-between">
              <div>
                <p className="badge-soft mb-3">Nos services</p>
                <h2 className="h2 text-anthracite-950">Une réponse adaptée à chaque besoin</h2>
                <p className="lead mt-2 max-w-2xl">Du transport en ambulance au transport spécialisé, nos équipes vous accompagnent avec professionnalisme.</p>
              </div>
              <Link to="/services" className="text-brand-700 inline-flex items-center gap-1 font-medium hover:gap-2 transition-all">
                Tous nos services <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: Ambulance, title: 'Ambulance', desc: 'Transport allongé avec équipe formée DEA.' },
              { icon: Car, title: 'VSL', desc: 'Véhicule sanitaire léger en position assise.' },
              { icon: Car, title: 'Taxi conventionné', desc: 'Pris en charge par l’Assurance Maladie.' },
              { icon: BedDouble, title: 'Transport lit hospitalier', desc: 'Véhicule équipé pour la continuité des soins.' },
              { icon: ShieldCheck, title: 'Transport bariatrique', desc: 'Matériel adapté jusqu’à 500 kg, en toute dignité.' },
              { icon: Heart, title: 'Dialyse, rééducation', desc: 'Trajets réguliers, accompagnement personnalisé.' },
            ].map((s, i) => (
              <Reveal key={s.title} delay={i * 60}>
                <Link to="/services" className="card-base p-5 flex flex-col gap-3 hover:border-brand-300">
                  <div className="h-11 w-11 rounded-xl bg-brand-50 text-brand-700 flex items-center justify-center">
                    <s.icon className="h-5 w-5" />
                  </div>
                  <h3 className="h3 text-anthracite-950">{s.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                  <span className="mt-auto text-sm text-brand-700 inline-flex items-center gap-1">Découvrir <ArrowRight className="h-4 w-4" /></span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SPECIALIZED TEASER */}
      <section className="section-pad bg-anthracite-100/60">
        <div className="container-app grid lg:grid-cols-2 gap-8 items-center">
          <Reveal>
            <div>
              <p className="badge-soft mb-3">Transports spécialisés</p>
              <h2 className="h2 text-anthracite-950">Un accompagnement digne et sécurisé</h2>
              <p className="lead mt-3 max-w-xl">Pour les transports complexes — bariatrique, lit hospitalier, transferts sensibles — nos équipes mobilisent un matériel adapté et une approche profondément humaine.</p>
              <ul className="mt-5 space-y-2 text-sm text-anthracite-700">
                <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand-600" /> Matériel spécifique et conforme</li>
                <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand-600" /> Équipes formées au transport spécialisé</li>
                <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand-600" /> Confort, discrétion et sécurité</li>
              </ul>
              <div className="mt-6 flex gap-3">
                <Link to="/transports-specialises">
                  <Button data-testid="home-specialized-cta">Découvrir</Button>
                </Link>
                <Link to="/devis">
                  <Button variant="outline">Demander un devis</Button>
                </Link>
              </div>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="relative aspect-[4/3] rounded-3xl overflow-hidden border bg-white shadow-card">
              <img
                src="https://images.pexels.com/photos/6754159/pexels-photo-6754159.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200"
                alt="Intérieur d'ambulance moderne"
                className="w-full h-full object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* REVIEWS TEASER */}
      <section className="section-pad">
        <div className="container-app">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end gap-4 justify-between">
              <div>
                <p className="badge-soft mb-3"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> Avis clients</p>
                <h2 className="h2 text-anthracite-950">La confiance, notre première mission</h2>
              </div>
              <Link to="/avis" className="text-brand-700 inline-flex items-center gap-1 font-medium hover:gap-2 transition-all">
                Voir tous les avis <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {(reviews.length ? reviews : Array.from({ length: 3 })).map((r, i) => (
              <Reveal key={i} delay={i * 80}>
                <article className="card-base p-5 h-full flex flex-col" data-testid="review-card">
                  <StarRating rating={r?.rating || 5} />
                  <p className="text-sm leading-relaxed mt-3 text-anthracite-900">
                    {r?.comment || 'Une équipe humaine et réactive, je recommande.'}
                  </p>
                  <div className="mt-4 pt-3 border-t text-xs text-muted-foreground">
                    {r?.author || 'Client'} • {r?.context || 'Transport sanitaire'}
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="relative overflow-hidden">
        <div className="container-app pb-16">
          <div className="relative rounded-3xl bg-gradient-to-r from-brand-700 to-brand-900 p-8 sm:p-12 text-white overflow-hidden">
            <div className="absolute -top-12 -right-12 w-80 h-80 rounded-full bg-brand-500/30 blur-3xl"></div>
            <div className="relative grid md:grid-cols-2 gap-6 items-center">
              <div>
                <h3 className="text-2xl sm:text-3xl font-heading font-semibold">Un transport à planifier ?</h3>
                <p className="text-white/80 mt-2 max-w-xl">Notre équipe vous répond rapidement et vous accompagne dans l'organisation de votre trajet.</p>
              </div>
              <div className="flex flex-wrap gap-3 md:justify-end">
                <a href={`tel:${MAIN_PHONE.replace(/\s/g, '')}`} className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-3 text-brand-800 font-medium hover:bg-anthracite-100"><Phone className="h-5 w-5" /> Appeler</a>
                <Link to="/devis" className="inline-flex items-center gap-2 rounded-xl bg-brand-500 hover:bg-brand-600 px-5 py-3 text-white font-medium"><FileText className="h-5 w-5" /> Demande de devis</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ icon: Icon, value, label }) {
  return (
    <div className="card-base p-4 flex items-center gap-3">
      <div className="h-10 w-10 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-xl font-heading font-semibold text-anthracite-950">
          <AnimatedCounter value={value} />
        </div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}
